/**
 * Validación de pedido mínimo para página de producto
 * Requiere mínimo 3 unidades para habilitar los botones de compra
 */

class ProductMinimumValidation {
  constructor() {
    this.minimumQuantity = 3;
    this.lastMeetsMinimum = null;
    this.validateTimeout = null;
    this.isValidating = false;
    this.cachedCartQuantity = null;
    this.cartFetchPromise = null;
    this.quantityInput = null;
    this.init();
  }

  init() {
    this.validateProductQuantity();
    this.listenToQuantityChanges();

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.updateCartCacheFromEvent(event);
        this.scheduleValidation();
      });
    }
  }

  scheduleValidation(delay = 120) {
    if (this.validateTimeout) {
      clearTimeout(this.validateTimeout);
    }
    this.validateTimeout = setTimeout(() => {
      this.validateTimeout = null;
      this.validateProductQuantity();
    }, delay);
  }

  updateCartCacheFromEvent(event) {
    if (!event?.cartData?.items || !Array.isArray(event.cartData.items)) return;

    this.cachedCartQuantity = event.cartData.items.reduce((sum, item) => {
      return sum + (parseInt(item.quantity, 10) || 0);
    }, 0);
  }

  getProductQuantityInput() {
    if (this.quantityInput && document.contains(this.quantityInput)) {
      return this.quantityInput;
    }

    this.quantityInput =
      document.querySelector(
        'product-info quantity-input input[type="number"], .product-form__quantity quantity-input input[type="number"]'
      ) || document.querySelector('quantity-input input[type="number"]');

    if (!this.quantityInput) {
      const productForm = document.querySelector('product-form form, form[data-type="add-to-cart-form"]');
      if (productForm) {
        this.quantityInput = productForm.querySelector(
          'quantity-input input, input[name="quantity"], input[type="number"]'
        );
      }
    }

    return this.quantityInput;
  }

  getProductQuantity() {
    const quantityInput = this.getProductQuantityInput();
    if (!quantityInput) return 0;
    return Math.max(0, parseInt(quantityInput.value, 10) || 0);
  }

  sumCartItems(cartData) {
    if (!cartData?.items || !Array.isArray(cartData.items)) return 0;

    return cartData.items.reduce((sum, item) => {
      return sum + (parseInt(item.quantity, 10) || 0);
    }, 0);
  }

  getCartQuantityFromDom() {
    let totalQuantity = 0;
    const quantityInputs = document.querySelectorAll(
      'cart-drawer-items quantity-input input[type="number"], cart-items quantity-input input[type="number"]'
    );

    quantityInputs.forEach((input) => {
      totalQuantity += parseInt(input.value, 10) || 0;
    });

    if (totalQuantity === 0) {
      const cartCountElement = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
      if (cartCountElement) {
        totalQuantity = parseInt(cartCountElement.textContent.trim(), 10) || 0;
      }
    }

    return Math.max(0, totalQuantity);
  }

  async fetchAndCacheCartQuantity() {
    if (this.cartFetchPromise) return this.cartFetchPromise;

    this.cartFetchPromise = (async () => {
      try {
        const response = await fetch(`${routes.cart_url}.js`);
        if (response.ok) {
          const cartData = await response.json();
          this.cachedCartQuantity = this.sumCartItems(cartData);
          return this.cachedCartQuantity;
        }
      } catch (error) {
        console.error('Error fetching cart:', error);
      }

      this.cachedCartQuantity = this.getCartQuantityFromDom();
      return this.cachedCartQuantity;
    })();

    try {
      return await this.cartFetchPromise;
    } finally {
      this.cartFetchPromise = null;
    }
  }

  async ensureCartQuantityCached() {
    if (this.cachedCartQuantity !== null) return this.cachedCartQuantity;
    return this.fetchAndCacheCartQuantity();
  }

  getTotalQuantity(cartQuantity) {
    const cartQty = Math.floor(parseFloat(cartQuantity) || 0);
    const prodQty = this.getProductQuantity();
    return Math.max(0, Math.floor(cartQty + prodQty));
  }

  applyValidation(totalQty) {
    const meetsMinimum = totalQty >= this.minimumQuantity;
    const dynamicCheckoutButtons = document.querySelectorAll(
      '.shopify-payment-button__button, .dynamic-checkout__content button, button[data-testid="buy-now-button"], .shopify-payment-button button, [data-shopify="dynamic-checkout"] button'
    );

    dynamicCheckoutButtons.forEach((button) => {
      if (meetsMinimum) {
        button.removeAttribute('disabled');
        button.classList.remove('button--disabled');
        button.style.opacity = '1';
        button.style.pointerEvents = 'auto';
      } else {
        button.setAttribute('disabled', 'disabled');
        button.classList.add('button--disabled');
        button.style.opacity = '0.5';
        button.style.pointerEvents = 'none';
      }
    });

    const newValue = String(totalQty);
    document.querySelectorAll('[id^="product-current-quantity-"]').forEach((qty) => {
      if (qty.textContent !== newValue) {
        qty.textContent = newValue;
      }
    });

    this.lastMeetsMinimum = meetsMinimum;
    const desiredDisplay = meetsMinimum ? 'none' : 'block';
    document.querySelectorAll('.product-minimum-message').forEach((msg) => {
      if (msg.style.display !== desiredDisplay) {
        msg.style.display = desiredDisplay;
      }
    });
  }

  async validateProductQuantity() {
    if (this.isValidating) {
      this.scheduleValidation();
      return;
    }
    this.isValidating = true;

    try {
      const cartQuantity = await this.ensureCartQuantityCached();
      const totalQty = this.getTotalQuantity(cartQuantity);
      this.applyValidation(totalQty);
    } finally {
      this.isValidating = false;
    }
  }

  listenToQuantityChanges() {
    // Un solo listener: quantity-input ya dispara "change" al pulsar +/-.
    document.addEventListener(
      'change',
      (event) => {
        if (!event.target.matches('quantity-input input, input[name="quantity"]')) return;

        const productForm = event.target.closest('product-form, form[data-type="add-to-cart-form"]');
        const productInfo = event.target.closest('product-info');
        if (productForm || productInfo) {
          this.scheduleValidation();
        }
      },
      { passive: true }
    );

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.quantityUpdate, () => {
        this.scheduleValidation();
      });
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('product-info, product-form')) {
      new ProductMinimumValidation();
    }
  });
} else if (document.querySelector('product-info, product-form')) {
  new ProductMinimumValidation();
}
